import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { jerseyApi, Synonym, SynonymCreate } from '../../api/jerseyApi';
import { useSnackbar } from '../../contexts/SnackbarContext';

interface SynonymManagerProps {
  entityType: 'color' | 'pattern';
  entityId: number;
  entityName: string;
  disabled?: boolean;
}

interface SynonymFormData {
  term: string;
  original_term: string;
}

const SynonymManager: React.FC<SynonymManagerProps> = ({
  entityType,
  entityId,
  entityName,
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSynonym, setEditingSynonym] = useState<Synonym | null>(null);
  const [formData, setFormData] = useState<SynonymFormData>({
    term: '',
    original_term: entityName,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  // Query synonyms for this entity
  const { data: synonyms = [] } = useQuery({
    queryKey: ['synonyms', entityType, entityId],
    queryFn: () => {
      const params = entityType === 'color' 
        ? { colour_id: entityId }
        : { pattern_id: entityId };
      return jerseyApi.getSynonyms(params);
    },
    enabled: !!entityId,
  });

  // Create synonym mutation
  const createMutation = useMutation({
    mutationFn: (data: SynonymCreate) => jerseyApi.createSynonym(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synonyms', entityType, entityId] });
      showSnackbar('Synonym created successfully', 'success');
      handleCloseDialog();
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to create synonym';
      showSnackbar(message, 'error');
    },
  });

  // Update synonym mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<SynonymCreate> }) =>
      jerseyApi.updateSynonym(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synonyms', entityType, entityId] });
      showSnackbar('Synonym updated successfully', 'success');
      handleCloseDialog();
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to update synonym';
      showSnackbar(message, 'error');
    },
  });

  // Delete synonym mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => jerseyApi.deleteSynonym(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['synonyms', entityType, entityId] });
      showSnackbar('Synonym deleted successfully', 'success');
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to delete synonym';
      showSnackbar(message, 'error');
    },
  });

  useEffect(() => {
    if (editingSynonym) {
      setFormData({
        term: editingSynonym.term,
        original_term: editingSynonym.original_term,
      });
    } else {
      setFormData({
        term: '',
        original_term: entityName,
      });
    }
  }, [editingSynonym, entityName]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.term.trim()) {
      newErrors.term = 'Synonym term is required';
    }

    if (!formData.original_term.trim()) {
      newErrors.original_term = 'Original term is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenDialog = (synonym?: Synonym) => {
    setEditingSynonym(synonym || null);
    setDialogOpen(true);
    setErrors({});
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSynonym(null);
    setErrors({});
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const synonymData: SynonymCreate = {
      term: formData.term.trim(),
      original_term: formData.original_term.trim(),
      ...(entityType === 'color' ? { colour_id: entityId } : { pattern_id: entityId }),
    };

    if (editingSynonym) {
      updateMutation.mutate({ id: editingSynonym.id, data: synonymData });
    } else {
      createMutation.mutate(synonymData);
    }
  };

  const handleDelete = (synonym: Synonym) => {
    if (window.confirm(`Are you sure you want to delete the synonym "${synonym.term}"?`)) {
      deleteMutation.mutate(synonym.id);
    }
  };

  const isMutating = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Synonyms for {entityName}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={disabled || isMutating}
          size="small"
        >
          Add Synonym
        </Button>
      </Box>

      {synonyms.length === 0 ? (
        <Alert severity="info">
          No synonyms defined. Add synonyms to help users find this {entityType} using alternative terms.
        </Alert>
      ) : (
        <List dense>
          {synonyms.map((synonym) => (
            <ListItem key={synonym.id} divider>
              <ListItemText
                primary={synonym.term}
                secondary={`Maps to: ${synonym.original_term}`}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  onClick={() => handleOpenDialog(synonym)}
                  disabled={disabled || isMutating}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => handleDelete(synonym)}
                  disabled={disabled || isMutating}
                  size="small"
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Synonym Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSynonym ? 'Edit Synonym' : 'Add New Synonym'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Synonym Term"
              value={formData.term}
              onChange={(e) => setFormData(prev => ({ ...prev, term: e.target.value }))}
              error={!!errors.term}
              helperText={errors.term || 'Enter an alternative term that users might use'}
              margin="normal"
              autoFocus
            />
            <TextField
              fullWidth
              label="Original Term"
              value={formData.original_term}
              onChange={(e) => setFormData(prev => ({ ...prev, original_term: e.target.value }))}
              error={!!errors.original_term}
              helperText={errors.original_term || 'The canonical term this synonym maps to'}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isMutating}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isMutating}
            startIcon={<SaveIcon />}
          >
            {isMutating ? 'Saving...' : (editingSynonym ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SynonymManager;
